import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { IntlProvider } from "react-intl";
import loadMessages from "../translate";
import { LocaleContextProps } from "../types";
import { useUserContext } from "./UserContext";

const DEFAULT_LOCALE = "en-US";

const LocaleContext = createContext<LocaleContextProps | undefined>(undefined);

export const useLocaleContext = (): LocaleContextProps => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error(
      "useLocaleContext must be used within a LocaleContextProvider",
    );
  }
  return context;
};

interface LocaleContextProviderProps {
  children: ReactNode;
}

export function LocaleContextProvider({
  children,
}: Readonly<LocaleContextProviderProps>): JSX.Element {
  // Consume user data from UserContext — no duplicate API call
  const { user } = useUserContext();

  const resolvedLocale = useMemo(
    () => (user?.user?.locale ?? DEFAULT_LOCALE).replace("_", "-"),
    [user?.user?.locale],
  );

  const [locale, setLocale] = useState(DEFAULT_LOCALE);
  const [messages, setMessages] = useState<Record<string, string>>(() =>
    loadMessages(DEFAULT_LOCALE),
  );

  // Sync locale when user data resolves
  useEffect(() => {
    setLocale(resolvedLocale);
    setMessages(loadMessages(resolvedLocale));
  }, [resolvedLocale]);

  const changeLocale = (nextLocale: string): void => {
    setLocale(nextLocale);
    setMessages(loadMessages(nextLocale));
  };

  const value = useMemo(
    () => ({
      locale,
      setLocale: changeLocale,
    }),
    [locale],
  );

  return (
    <LocaleContext.Provider value={value}>
      <IntlProvider
        locale={locale || DEFAULT_LOCALE}
        defaultLocale={DEFAULT_LOCALE}
        messages={messages}
      >
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
}
